import { useFetch } from "@/hooks/FetchHook";
import { useGeoRadiusFilter } from "@/hooks/useGeoRadiusFilter";
import { prepareUnifiedDataPostRequestBody } from "@/components/UI/TableToolbar/Filtering/filtersApiUtil";
import {
  getGeoFilterField,
  hasGeoFilterSupport,
} from "@/components/UI/TableToolbar/Filtering/geoFilterUtil";
import { buildDataEndpointSuffix } from "@/components/Templates/constants";
import SiteDirectoryMap from "./components/SiteDirectoryMap";
import type { ExtraTabComponentProps } from "@/components/Templates/types";
import type { SiteDirectoryRow } from "./types";

/** Backend max_page_size — one request covers the whole demo portfolio. */
const MAP_MARKERS_PAGE_SIZE = 2000;

type MarkersResponse = {
  results: SiteDirectoryRow[];
  count: number;
};

/**
 * Map view of the Site Directory. Markers are fetched with the SAME request
 * body the table uses (same `global_filters`), so filters applied anywhere on
 * the screen — including the radius filter the map itself emits — scope the
 * markers automatically.
 */
export function SiteMapTab({
  filters = {},
  setFilters,
  metadata,
  datasetName = "portfolio-sites",
  endpointPrefix,
  geoEditRequestToken,
  onGeoEditRequestConsumed,
}: ExtraTabComponentProps) {
  const geoField = getGeoFilterField(metadata?.filters as any);
  // The radius tool only renders when the backend advertises geo filter
  // support in `/filters/` metadata, so environments where within_radius
  // hasn't shipped yet get the map view without a filter the API would 500 on.
  const radiusToolEnabled = hasGeoFilterSupport(metadata?.filters as any);
  const { geoCondition, applyGeoFilter, updateGeoFilter, clearGeoFilter } =
    useGeoRadiusFilter({ geoField, filters, setFilters });

  const markersQuery = useFetch({
    endpointPrefix,
    endpointSuffix: buildDataEndpointSuffix(datasetName, endpointPrefix),
    method: "POST",
    body: {
      dataset_type: datasetName,
      ...prepareUnifiedDataPostRequestBody({
        page: 1,
        pageSize: MAP_MARKERS_PAGE_SIZE,
        global_filters: filters,
        allFiltersData: metadata?.filters ?? {},
      }),
    },
  });
  const response = markersQuery.data as MarkersResponse | undefined;

  return (
    <SiteDirectoryMap
      sites={response?.results ?? []}
      loading={markersQuery.isLoading || markersQuery.isFetching}
      totalCount={response?.count ?? 0}
      geoCondition={geoCondition}
      radiusToolEnabled={radiusToolEnabled}
      onApplyGeoFilter={applyGeoFilter}
      onUpdateGeoFilter={updateGeoFilter}
      onClearGeoFilter={clearGeoFilter}
      geoEditRequestToken={geoEditRequestToken}
      onGeoEditRequestConsumed={onGeoEditRequestConsumed}
    />
  );
}
